/** Flag uncited uses of document-derived water/environment figures in the final narrative. */
import type { EvidenceSection, SiteEvidenceDocument } from '../../types/siteEvidence';
import type { SanityFinding, SiteRagReport } from '../../types/siteRag';
import { flattenLeafValues } from '../sed/docConflicts';

type Fact = { section: string; field: string; value: number; docId: string; docName: string };
const DOMAIN = /\b(?:water|wastewater|sewer|wetlands?|creeks?|ponds?|flood|MGD|gallons?|acres?)\b/i;

function numericFacts(sed: SiteEvidenceDocument): Fact[] {
  const facts: Fact[] = [];
  for (const section of ['waterResource', 'environmental', 'flood'] as const) {
    const slot = sed[section] as EvidenceSection<unknown> | undefined;
    if (!slot) continue;
    const leaves = flattenLeafValues(slot.data);
    const add = (field: string, docId: string, docName?: string): void => {
      const value = leaves[field];
      if (typeof value === 'number' && value !== 0) facts.push({ section, field, value, docId, docName: docName ?? sed.uploadedDocuments?.find(d => d.docId === docId)?.fileName ?? docId });
    };
    for (const [field, source] of Object.entries(slot.docFieldSources ?? {})) {
      // An amendment may have replaced this leaf since extraction. Its old
      // source must not be advertised as the provenance of the corrected value.
      if (source.value === leaves[field]) add(field, source.docId, source.docName);
    }
    for (const source of slot.sourceContributions ?? []) {
      if (!source.documentId || source.reviewState === 'rejected') continue;
      for (const field of Object.keys(leaves)) {
        if (slot.docFieldSources?.[field]) continue;
        if (source.path === '*' || field === source.path || field.startsWith(`${source.path}.`)) add(field, source.documentId);
      }
    }
    if (!slot.docFieldSources && (slot.status === 'verified-from-doc' || slot.provenance === 'doc')) {
      const docId = slot.provenanceDetail?.docId ?? slot.linkedDocumentRefs?.find(r => r.role === 'primary')?.docId;
      if (docId) {
        const primaryValues = flattenLeafValues(slot.provenanceDetail?.docValue);
        const hasSnapshot = Object.keys(primaryValues).length > 0;
        const contributors = new Set([...(slot.linkedDocumentIds ?? []), ...(slot.linkedDocumentRefs ?? []).map(r => r.docId), ...(slot.supportingDocIds ?? [])]);
        for (const [field, value] of Object.entries(leaves)) {
          // Legacy multi-source slots without a field snapshot cannot safely
          // attribute every numeric leaf to whichever document ranked first.
          if (hasSnapshot ? primaryValues[field] === value : contributors.size <= 1) add(field, docId);
        }
      }
    }
    for (const conflict of slot.docConflicts ?? []) {
      for (const witness of conflict.values) {
        if (typeof witness.value === 'number' && witness.value !== 0) facts.push({ section, field: conflict.field, value: witness.value, docId: witness.docId, docName: witness.docName });
      }
    }
  }
  return facts;
}

/** Restrict checks to reader-visible prose, excluding data, citations, diagnostics and embedded SEDs. */
function prose(value: unknown, path: string, out: { path: string; text: string }[], depth = 0): void {
  if (depth > 8 || value == null) return;
  if (typeof value === 'string') { out.push({ path, text: value }); return; }
  if (Array.isArray(value)) { value.forEach((v, i) => prose(v, `${path}.${i}`, out, depth + 1)); return; }
  if (typeof value === 'object') {
    for (const [key, v] of Object.entries(value)) {
      if (/^(?:source|url|reference|citation|evidence|raw|data|id|title|label)/i.test(key)) continue;
      prose(v, `${path}.${key}`, out, depth + 1);
    }
  }
}

export function checkDocumentFactCitations(report: SiteRagReport, sed: SiteEvidenceDocument): SanityFinding[] {
  const facts = numericFacts(sed);
  if (!facts.length) return [];
  const blocks: { path: string; text: string }[] = [];
  for (const key of ['executiveSummary', 'waterAnalysis', 'environmentalAnalysis', 'floodAnalysis', 'sitingNotes', 'supplyAvailability', 'wastewaterStormwater', 'keyFindings', 'recommendations', 'categories']) {
    prose((report as unknown as Record<string, unknown>)[key], key, blocks);
  }
  const findings: SanityFinding[] = [];
  const seen = new Set<string>();
  for (const block of blocks) {
    for (const sentence of block.text.split(/(?<=[.!?])\s+|\n+/)) {
      if (!DOMAIN.test(sentence)) continue;
      const values = new Set((sentence.match(/\b\d[\d,]*(?:\.\d+)?\b/g) ?? []).map(s => Number(s.replace(/,/g, ''))));
      for (const fact of facts) {
        if (!values.has(fact.value)) continue;
        // A valid citation must resolve to this document, not merely exist.
        const direct = sentence.includes(`doc://${fact.docId}`) || (fact.docName !== fact.docId && sentence.includes(fact.docName));
        const referenced = [...sentence.matchAll(/\[Ref\s+(\d+)\]/gi)].some(m => {
          const ref = report.references?.[Number(m[1]) - 1];
          return ref?.documentId === fact.docId || ref?.url?.includes(`doc://${fact.docId}`);
        });
        const key = `${block.path}:${fact.docId}:${fact.field}`;
        if (direct || referenced || seen.has(key)) continue;
        seen.add(key);
        findings.push({
          id: `doc-fact-citation-${findings.length + 1}`, category: 'citations', section: block.path,
          finding: `The narrative uses ${fact.value} from ${fact.docName} (${fact.field}) without a citation to that document.`,
          suggestion: `Cite ${fact.docName} at the figure and retain the source's date, author and project scope.`,
          severity: 'medium', evidence: sentence.slice(0, 500),
        });
        if (findings.length >= 30) return findings;
      }
    }
  }
  return findings;
}
